import React, { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BellRing, Building2, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useDingOnPedido } from "@/hooks/useDingOnPedido";
import useSoundStore from "@/store/soundStore";
import { getInitials } from "@/lib/format";
import StoreStatusBadge from "./StoreStatusBadge";

const ALL_COMPANIES = "__all__";

/**
 * Barra superior fixa: menu (celular), marca, empresa em foco, status da
 * loja, pedidos aguardando (com alerta sonoro) e conta do usuário.
 * `pendingCount` vem do AdminPanel (usePedidoStatus), que também alimenta a
 * barra inferior.
 */
const Header = ({
  setActiveTab,
  setSidebarOpen,
  setOrderFilterStatus,
  pendingCount = 0,
}) => {
  const {
    user,
    logout,
    company,
    companyName,
    companies,
    isGlobalOwner,
    selectedCompanyCode,
    selectCompany,
  } = useAuth();
  const soundMuted = useSoundStore((state) => state.muted);
  const { retryPlay } = useDingOnPedido(pendingCount, "./assets/sounds/cash.mp3", {
    autoRetry: true,
    retryMs: 10000,
    maxRetries: 12,
    playOnIncrease: false,
    muted: soundMuted,
  });

  useEffect(() => {
    retryPlay(pendingCount > 0);
  }, [pendingCount]);

  const openPendingOrders = () => {
    if (setOrderFilterStatus) {
      setOrderFilterStatus("Aguardando");
    }
    setActiveTab("orders");
  };

  return (
    <header className="sticky top-0 z-30 border-b bg-card/90 shadow-[0_1px_8px_rgba(31,46,30,0.04)] backdrop-blur-xl">
      <div className="flex h-16 items-center justify-between gap-2 px-3.5 md:px-6">
        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menu completo"
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border bg-surface text-foreground transition-colors hover:bg-muted lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 items-center gap-2">
            <img
              src="/assets/logo_cardapio.jpg"
              alt="Cardápio Bonfim"
              className="h-9 w-9 flex-shrink-0 rounded-full bg-white object-contain md:h-10 md:w-10"
            />
            <div className="hidden min-w-0 flex-col min-[360px]:flex">
              <span className="font-display text-base font-semibold leading-tight text-foreground">
                Bonfim
              </span>
              <span className="truncate text-[11px] font-semibold text-muted-foreground">
                Gestor Cardápio
              </span>
            </div>
          </div>

          {/* Empresa atual (owner global escolhe a empresa) */}
          {isGlobalOwner ? (
            <Select
              value={selectedCompanyCode || ALL_COMPANIES}
              onValueChange={(value) =>
                selectCompany(value === ALL_COMPANIES ? null : value)
              }
            >
              <SelectTrigger
                className="ml-1 h-9 w-[130px] bg-surface text-sm sm:w-[220px]"
                title="Empresa em foco"
              >
                <Building2 className="mr-1 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <SelectValue placeholder="Empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_COMPANIES}>Todas as empresas</SelectItem>
                {companies.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.name || item.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            companyName && (
              <div
                className="ml-2 hidden max-w-[240px] items-center gap-1.5 rounded-xl border bg-surface px-2.5 py-1.5 text-sm text-foreground md:flex"
                title="Empresa vinculada ao seu usuário"
              >
                <Building2 className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="truncate font-semibold">{companyName}</span>
              </div>
            )
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          <StoreStatusBadge company={company} className="hidden min-[380px]:inline-flex" />

          {pendingCount > 0 && (
            <button
              type="button"
              onClick={openPendingOrders}
              title={`${pendingCount} pedido(s) aguardando`}
              aria-label={`${pendingCount} pedido(s) aguardando`}
              className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-ambient transition-colors hover:bg-brand-dark"
            >
              <BellRing className="h-5 w-5" />
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-terra px-1 text-[11px] font-bold text-white ring-2 ring-card">
                {pendingCount > 99 ? "99+" : pendingCount}
              </span>
            </button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Conta do usuário"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary font-display text-xs font-bold text-primary ring-2 ring-border transition-shadow hover:ring-primary/40"
              >
                {getInitials(user)}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="space-y-0.5">
                <span className="block truncate text-sm">{user?.email}</span>
                {companyName && (
                  <span className="block truncate text-xs font-normal text-muted-foreground">
                    {companyName}
                  </span>
                )}
              </DropdownMenuLabel>
              {company && (
                <div className="flex items-center justify-between px-2 pb-1.5 min-[380px]:hidden">
                  <span className="text-xs text-muted-foreground">Loja</span>
                  <StoreStatusBadge company={company} />
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
